import { type FC } from "react";
import {
  Box,
  IconButton,
  Slide,
  Typography,
  useMediaQuery,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import PictureInPictureAltIcon from "@mui/icons-material/PictureInPictureAlt";
import LabelIcon from "@mui/icons-material/Label";
import LabelOffIcon from "@mui/icons-material/LabelOff";
import { useLabelsVisibility } from "@/shared/hooks";
import { SeekBar } from "./SeekBar";
import type { PlayerTimecode } from "./playerTimecode";
import { VolumeControl } from "./VolumeControl";
import { QualityMenu, SpeedMenu } from "./SettingsMenus";
import { formatPlayerTime } from "./formatPlayerTime";
import { playerFooterControlPadding } from "./playerFooterControlPadding";
import type { useVideoEngine } from "./useVideoEngine";
import { useTranslation } from "react-i18next";

type Engine = ReturnType<typeof useVideoEngine>;

interface PlayerFooterProps {
  src?: string | null;
  state: Engine["state"];
  actions: Engine["actions"];
  visible: boolean;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
  onTogglePip: () => void;
  timecodes?: PlayerTimecode[] | null;
}

// Ближе этого к краю окна считаем, что зритель смотрит «в прямом эфире».
const LIVE_EDGE_TOLERANCE_SECONDS = 8;

export const PlayerFooter: FC<PlayerFooterProps> = ({
  src,
  state,
  actions,
  visible,
  fullscreen,
  onToggleFullscreen,
  onTogglePip,
  timecodes,
}) => {
  // На краю эфира ползунок стоит справа и бейдж просто горит: окно HLS
  // скользит, и «живые» currentTime/duration иначе дёргаются каждую секунду.
  // Отставание показываем только когда зритель отмотал назад по DVR.
  const atLiveEdge =
    state.live &&
    state.duration - state.currentTime <= LIVE_EDGE_TOLERANCE_SECONDS;
  const { t } = useTranslation();
  const isNarrow = useMediaQuery("(max-width:450px)");
  const [labelsVisible, toggleLabelsVisibility] = useLabelsVisibility();
  const hasTimecodes = Boolean(timecodes && timecodes.length > 0);

  return (
    <Slide direction="up" in={visible} appear={false}>
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          pb: 0.5,
          pointerEvents: visible ? "auto" : "none",
          px: { xs: 1, sm: 2, md: 3 },
          "@media (max-width: 450px)": { px: 0.5 },
          "&:before": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 64,
            pointerEvents: "none",
            backgroundImage:
              "linear-gradient(transparent, rgba(0,0,0,0.6), rgba(0,0,0,0.9))",
          },
        }}
      >
        {/* Remounting on src change clears any in-progress drag state so a
            stale drag position is never committed against the new video. */}
        {/* В живом эфире сикбар остаётся: это DVR-окно HLS, по нему можно
            отмотать назад. Вместо «время / длительность» (длительность это
            лишь окно сегментов) показываем LIVE и отставание от края. */}
        <SeekBar
          key={src ?? ""}
          currentTime={atLiveEdge ? state.duration : state.currentTime}
          duration={state.duration}
          buffered={state.buffered}
          onSeek={actions.seek}
          timecodes={timecodes}
        />
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minHeight: 56,
          }}
        >
          <IconButton
            size="large"
            onClick={actions.togglePlay}
            sx={(theme) => ({
              color: "#fff",
              ...playerFooterControlPadding(theme),
            })}
          >
            {state.paused ? <PlayArrowIcon /> : <PauseIcon />}
          </IconButton>
          {state.live ? (
            <Typography
              variant="caption"
              sx={{
                color: "#fff",
                ml: 0.5,
                px: 0.75,
                borderRadius: 0.5,
                bgcolor: "error.main",
                fontWeight: 700,
                letterSpacing: "0.04em",
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            >
              LIVE
              {atLiveEdge
                ? ""
                : ` -${formatPlayerTime(state.duration - state.currentTime)}`}
            </Typography>
          ) : (
            <Typography
              variant="caption"
              sx={{
                color: "#fff",
                ml: 0.5,
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              {formatPlayerTime(state.currentTime)} /{" "}
              {formatPlayerTime(state.duration)}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <VolumeControl
            volume={state.volume}
            muted={state.muted}
            onToggleMute={actions.toggleMute}
            onSetVolume={actions.setVolume}
          />
          <QualityMenu
            levels={state.levels}
            currentLevel={state.currentLevel}
            autoLevel={state.autoLevel}
            onSetLevel={actions.setLevel}
          />
          {hasTimecodes && (
            <IconButton
              size="large"
              onClick={toggleLabelsVisibility}
              sx={(theme) => ({
                color: "#fff",
                opacity: labelsVisible ? 1 : 0.6,
                ...playerFooterControlPadding(theme),
              })}
              aria-label={
                labelsVisible
                  ? t("shared.player.hideLabels", {
                      defaultValue: "Скрыть метки",
                    })
                  : t("shared.player.showLabels", {
                      defaultValue: "Показать метки",
                    })
              }
            >
              {labelsVisible ? <LabelIcon /> : <LabelOffIcon />}
            </IconButton>
          )}
          {!isNarrow && (
            <SpeedMenu
              playbackRate={state.playbackRate}
              onSetRate={actions.setPlaybackRate}
            />
          )}
          {!isNarrow && (
            <IconButton
              size="large"
              onClick={onTogglePip}
              sx={(theme) => ({
                color: "#fff",
                ...playerFooterControlPadding(theme),
              })}
              aria-label={t("shared.player.pip", {
                defaultValue: "Картинка в картинке",
              })}
            >
              <PictureInPictureAltIcon />
            </IconButton>
          )}
          <IconButton
            size="large"
            onClick={onToggleFullscreen}
            sx={(theme) => ({
              color: "#fff",
              ...playerFooterControlPadding(theme),
            })}
          >
            {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
      </Box>
    </Slide>
  );
};
